import React from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Box, Typography, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";

/**
 * NotFound — 404 page for unknown routes within a tenant layout
 * (Admin / Client / Donor / Worker / SuperAdmin). Replaces the silent
 * redirect-to-/index that was hiding bad URLs (JOB-3225 + JOB-3220).
 *
 * The parent layout supplies the sidebar + header; this component fills
 * only the main content pane, so it looks consistent with the rest of
 * the app and keeps the user one click away from getting unstuck.
 */
export default function NotFound({ homePath }) {
  const theme = useTheme();
  const { CompanyUrl } = useParams();
  const isDark = theme.palette.mode === "dark";
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";

  // Fallback home route when the layout doesn't supply one. SuperAdmin
  // gets /superadmin, everyone else lands back at their company's /index.
  const fallbackHome = homePath
    ? homePath
    : pathname.startsWith("/superadmin")
    ? "/superadmin/company"
    : CompanyUrl
    ? `/${CompanyUrl}/index`
    : "/";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        textAlign: "center",
        px: 3,
      }}
    >
      <Typography
        variant="h1"
        sx={{
          fontSize: { xs: "5rem", md: "7rem" },
          fontWeight: 700,
          lineHeight: 1,
          color: isDark ? "#3d4f66" : "#cbd5e1",
          mb: 2,
        }}
      >
        404
      </Typography>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 600,
          color: isDark ? "#e8eaed" : "#0f2b46",
          mb: 1.5,
        }}
      >
        Page not found
      </Typography>
      <Typography
        sx={{
          color: isDark ? "#94a3b8" : "#64748b",
          maxWidth: 520,
          mb: 4,
        }}
      >
        {pathname ? (
          <>
            We couldn&rsquo;t find the page at{" "}
            <Box component="code" sx={{ px: 0.5, borderRadius: 0.5, background: isDark ? "#1a2332" : "#f1f5f9" }}>
              {pathname}
            </Box>
            . The URL may be mistyped or the page may have moved.
          </>
        ) : (
          "The URL may be mistyped or the page may have moved."
        )}
      </Typography>
      <Button
        component={RouterLink}
        to={fallbackHome}
        variant="contained"
        size="large"
        sx={{
          backgroundColor: "#2563eb",
          color: "#fff",
          "&:hover": { backgroundColor: "#1d4ed8" },
          textTransform: "none",
        }}
      >
        Back to dashboard
      </Button>
    </Box>
  );
}
